import axios from 'axios'
import URI from 'urijs'

import Logger from '@/logger'
import { ERRORS, CLIENT_QUERY_TIMEOUT } from '@/constants'
import settings from '@/settings'
import { AsyncTaskError, ValidationError } from '../errors'

const STATUS = {
  ok: 'ok',
  redirect: 'redirect',
  processing: 'processing',
  error: 'error'
}

/**
 * Make FormData from plain object
 * @param {Object} data
 * @returns FormData
 */
const makeFormData = (data) => {
  const formData = new FormData()
  Object.keys(data).forEach((key) => {
    let value = data[key]
    if (value && typeof value === 'object') {
      value = JSON.stringify(value)
    }

    // append for support ie11 ("set" not supported)
    formData.append(key, value)
  })
  return formData
}

/**
 * Create absolute url from relative
 * @param {string} urlPart
 */
const makeAbsoluteUri = (urlPart) => {
  // create absolute url if needed
  let url = new URI(urlPart)
  if (!url.domain()) {
    url = url.absoluteTo(window.location.origin)
  }
  url = url.addQuery({ _: new Date().getTime() }) // prevent browser cache
  return url.toString()
}

const timeout = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export default class RestClient {
  client;
  defaults = {
    axiosClientConfig: {}
  };

  /**
   * Create a client
   * @param {ClientOptions} options
   */
  constructor (options = {}) {
    const { baseURL, axiosClientConfig } = {
      ...this.defaults,
      ...options
    }

    if (!baseURL) {
      throw new Error('baseURL is required')
    }

    this.client = axios.create({
      baseURL,
      timeout: CLIENT_QUERY_TIMEOUT,
      ...axiosClientConfig,
      headers: {
        'X-Requested-With': 'XMLHttpRequest', // for compatibility reasons
        ...axios.defaults.headers
      }
    })

    const responseLogger = (response) => {
      const { status, data } = response
      if (!data || (data && data.status !== STATUS.processing)) { // skip logging for processing status
        Logger.debug(`[API] Request completed. Status: ${status} Response:`, data)
      }
      return response
    }

    const errorLogger = (error) => {
      if (error.response) {
        const { status } = error.response
        Logger.error(`[API] Request failed. Status: ${status}.`)
      }
      return Promise.reject(error)
    }
    this.client.interceptors.response.use(responseLogger, errorLogger)
  }

  /**
   * Create async task and wait for response
   * @param {string} url
   * @param {Object} params
   */
  async createAsyncTask (url, params = {}, options = {}) {
    const method = options.method || 'POST'

    const response = (method.toUpperCase() === 'GET')
      ? await this.client.get(url, params)
      : await this.client.post(url, makeFormData(params))

    const checkStatusOptions = {
      maxTime: (options.maxTime || settings.get('PENDING_OPERATION_JAVASCRIPT_TIMEOUT', 10)) * 1000,
      maxWaitTime: (options.maxWaitTime || settings.get('PENDING_OPERATION_JAVASCRIPT_WAIT', 1)) * 1000,
      startTime: new Date().getTime(),
      retriesCount: 0
    }
    return this.checkTaskResult(response.data, checkStatusOptions)
  }

  /**
   * Check async task execution status
   * @param {AsyncTaskResponse} taskData
   * @param {Object} options
   */
  async checkTaskResult (taskData, options = {}) {
    const handlers = {
      [STATUS.ok]: this.handleSuccess,
      [STATUS.processing]: this.handleProcessing,
      [STATUS.redirect]: this.handleRedirect,
      [STATUS.error]: this.handleError
    }
    const handler = handlers[taskData.status]
    if (!handler) {
      throw new AsyncTaskError(ERRORS.UNKNOWN_ERROR)
    }
    return handler.call(this, taskData, options)
  }

  /**
   * Handle 'success' task response
   * @param {AsyncTaskResponse} taskData
   * @param {Object} options
   */
  async handleSuccess (taskData, options = {}) {
    if (options.successUrl) {
      window.location.assign(options.successUrl)
    } else if (taskData.success_url) {
      window.location.assign(taskData.success_url)
    } else {
      return taskData.data
    }
  }

  /**
   * Handle 'processing' task response
   * @param {AsyncTaskResponse} taskData
   * @param {Object} options
   */
  async handleProcessing (taskData, options = {}) {
    const { maxTime, maxWaitTime, startTime } = options
    const retriesCount = options.retriesCount || 0
    const now = new Date().getTime()
    const isFirstTime = retriesCount === 0
    const stillProcessing = (now - startTime) < maxTime

    if (stillProcessing) {
      if (!isFirstTime) {
        await timeout(maxWaitTime)
      }

      const url = makeAbsoluteUri(taskData.status_url)
      const response = await this.client.get(url.toString())
      return this.checkTaskResult(response.data, {
        ...options,
        retriesCount: retriesCount + 1
      })
    } else {
      throw new AsyncTaskError(ERRORS.TIMEOUT_ERROR)
    }
  }

  /**
   * Handle 'redirect' task status
   * @param {AsyncTaskResponse} taskData
   * @param {Object} options
   */
  async handleRedirect (taskData, options = {}) {
    if (taskData.redirect_url) {
      window.location.assign(taskData.redirect_url)
    } else {
      Logger.warn('Empty redirect_url parameter') // TODO: what next ???
    }
  }

  /**
   * Handle 'error' task status
   * @param {AsyncTaskResponse} taskData
   * @param {Object} options
   * @throws {ValidationError | AsyncTaskError}
   */
  async handleError (taskData, options = {}) {
    const errorMessage = taskData.error || null
    const errorCode = taskData.error_code || null
    const errorContext = taskData.error_context

    const validationErrors = taskData.errors || null
    const validationErrorMessage = (taskData.data && taskData.data.error_message) || null

    // if errors || error_message that validation error
    if (validationErrors || validationErrorMessage) {
      throw new ValidationError({
        errors: validationErrors,
        errorMessage: validationErrorMessage
      })
    } else {
      let errorData = null
      if (errorMessage || errorCode) {
        errorData = {
          code: errorCode,
          message: errorMessage,
          context: errorContext
        }
      }
      throw new AsyncTaskError(errorData || ERRORS.UNKNOWN_ERROR)
    }
  }
}
